import { useState } from "react";
import {
	Control,
	Controller,
	FormState,
	UseFormSetValue,
	UseFormWatch,
	UseFormTrigger,
	UseFormGetValues,
} from "react-hook-form";
import { useStoreState } from "ezStore";
import { CargoActionForm, CargoForm } from "types/index";
import { Card, Checkbox, FormGroup, Input } from "@cofano/cck";
import NumberInput from "components/cck/NumberInput";
import FormField from "components/cck/FormField";
import ProductSelect from "view/products/components/select/ProductSelect";
import Actions from "view/cargos/components/createEdit/Actions";
import { CargoCreateEditDialogProps } from "view/cargos/components/dialogs/CargoCreateEditDialog";
import WeightToleranceTypeSelect from "components/selects/WeightToleranceTypeSelect";
import WeightOptionTypeSelect from "components/selects/WeightOptionTypeSelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import { CallType, RelationType, WeightToleranceType } from "__generated__/graphql";
import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language } from "utils";
import { intl } from "LocaleWrapper";
import {
	convertWeightAndVolume,
	defaultCargoAction,
	distributeWeightAndVolumeOverActions,
} from "view/cargos/utils/CargoUtils";
import DeliveryConditionSelect from "view/deliveryconditions/components/selects/DeliveryConditionSelect";
import { FormControlLabel, Switch, Grid2 as Grid } from "@mui/material";

type CargoCreateEditCustomContentProps = {
	defaultIncludedActionTypes?: CallType[];
	watch: UseFormWatch<CargoForm>,
	formState: FormState<CargoForm>,
	control: Control<CargoForm>,
	setValue: UseFormSetValue<CargoForm>
	validFrom?: number | null,
	validUntil?: number | null,
	trigger: UseFormTrigger<CargoForm>;
	getValues: UseFormGetValues<CargoForm>;
	validateCargoActions: () => boolean;
};

const CargoCreateEditCustomContent = ({
	defaultIncludedActionTypes,
	watch,
	formState,
	control,
	setValue,
	validFrom,
	validUntil,
	trigger,
	getValues,
	validateCargoActions,
}: Omit<CargoCreateEditDialogProps, "type"> & CargoCreateEditCustomContentProps) => {
	const session = useStoreState(store => store.session);
	const isCalculateWeightAndVolume = hasPluginPermission(session, "cargoConvertQuantities");
	const isCalculateWeightAndVolumeForActions = hasPluginPermission(session, "cargoFillQuantitiesOnCargoActions");

	const supplier = watch("supplier");

	const actions = watch("actions");
	const weightToleranceType = watch("weightToleranceType");
	const isTemperatureControlRequired = watch("isTemperatureControlRequired");

	const densityKgM3 = watch("product.densityKgM3") || 0;

	const nominatedWeight = watch("nominatedWeight");
	const nominatedVolume = watch("nominatedVolume");
	const declaredFinalWeight = watch("declaredFinalWeight");

	const isWeightPrimary = watch("isWeightPrimary");
	const isVolumePrimary = watch("isVolumePrimary");

	const [ convertCargoQuantities, setConvertCargoQuantities ] = useState(isCalculateWeightAndVolume);
	const [ fillCargoActionQuantities, setFillCargoActionQuantities ] = useState(isCalculateWeightAndVolumeForActions);

	const updateActionQuantities = (weight?: number, volume?: number, shouldConvert?: boolean) => {
		if (!fillCargoActionQuantities) {
			return;
		}

		const useConvert = shouldConvert ?? convertCargoQuantities;
		const currentActions = getValues("actions");

		let effectiveWeight = weight;
		let effectiveVolume = volume;

		if (declaredFinalWeight != null) {
			effectiveWeight = declaredFinalWeight;
			effectiveVolume = useConvert ? Number(Number(declaredFinalWeight / densityKgM3).toFixed(3)) : undefined;
		}

		const updatedActions = distributeWeightAndVolumeOverActions(
			currentActions,
			(isWeightPrimary || useConvert) ? effectiveWeight : undefined,
			(isVolumePrimary || useConvert) ? effectiveVolume : undefined,
		);

		setValue("actions", updatedActions);
	};

	const handleNominatedQuantityChange = (quantityType: "weight" | "volume", value?: number) => {
		// Can only convert if exactly one quantity is primary
		const canConvert = convertCargoQuantities && !(isWeightPrimary && isVolumePrimary) && !!densityKgM3;

		if (quantityType === "weight") {
			// Auto-convert volume if enabled and allowed
			if (canConvert && value != null) {
				const { volume } = convertWeightAndVolume("weight", densityKgM3, value);
				setValue("nominatedVolume", volume);
				updateActionQuantities(value, volume);
			} else {
				updateActionQuantities(value, nominatedVolume);
			}
		} else {
			// Auto-convert weight if enabled and allowed
			if (canConvert && value != null) {
				const { weight } = convertWeightAndVolume("volume", densityKgM3, value);
				setValue("nominatedWeight", weight);
				updateActionQuantities(weight, value);
			} else {
				updateActionQuantities(nominatedWeight, value);
			}
		}
	};

	const handleConvertQuantitiesToggle = (shouldConvert: boolean) => {
		setConvertCargoQuantities(shouldConvert);

		// Can only convert if exactly one quantity is primary
		if (isWeightPrimary && isVolumePrimary) {
			return;
		}
		if (!isWeightPrimary && !isVolumePrimary) {
			return;
		}

		if (shouldConvert) {
			// Convert the non-primary quantity from the primary one
			if (isWeightPrimary && nominatedWeight != null) {
				const { volume } = convertWeightAndVolume("weight", densityKgM3, nominatedWeight);
				setValue("nominatedVolume", volume);
				updateActionQuantities(nominatedWeight, volume, true);
			} else if (isVolumePrimary && nominatedVolume != null) {
				const { weight } = convertWeightAndVolume("volume", densityKgM3, nominatedVolume);
				setValue("nominatedWeight", weight);
				updateActionQuantities(weight, nominatedVolume, true);
			}
		} else {
			// When disabling convert, clear the non-primary quantity
			if (isWeightPrimary && !isVolumePrimary) {
				setValue("nominatedVolume", undefined);
				updateActionQuantities(nominatedWeight, undefined, false);
			} else if (isVolumePrimary && !isWeightPrimary) {
				setValue("nominatedWeight", undefined);
				updateActionQuantities(undefined, nominatedVolume, false);
			}
		}
	};

	const handleFillQuantitiesToggle = (shouldFill: boolean) => {
		setFillCargoActionQuantities(shouldFill);
		const currentActions = getValues("actions");

		if (!shouldFill) {
			// Clear all action quantities
			const updatedActions = currentActions.map(action => ({
				...action,
				weight: null,
				volume: null,
			}));
			setValue("actions", updatedActions);
		} else {
			// Fill with current nominated quantities
			const useConvert = convertCargoQuantities;
			const effectiveWeight = declaredFinalWeight ?? nominatedWeight;
			const effectiveVolume = declaredFinalWeight != null && useConvert
				? Number(Number(declaredFinalWeight / densityKgM3).toFixed(3))
				: nominatedVolume;

			const updatedActions = distributeWeightAndVolumeOverActions(
				currentActions,
				(isWeightPrimary || useConvert) ? effectiveWeight : undefined,
				(isVolumePrimary || useConvert) ? effectiveVolume : undefined,
			);
			setValue("actions", updatedActions);
		}
	};

	const handlePrimaryCheckboxChange = (quantityType: "weight" | "volume") => {
		if (quantityType === "weight") {
			// If unchecking weight primary while volume is primary and convert is enabled, convert from volume
			if (convertCargoQuantities && isVolumePrimary && nominatedVolume != null) {
				const { weight } = convertWeightAndVolume("volume", densityKgM3, nominatedVolume);
				setValue("nominatedWeight", weight);
				updateActionQuantities(weight, nominatedVolume);
			} else {
				setValue("nominatedWeight", undefined);
				// If weight was being used to convert volume, clear volume too
				if (convertCargoQuantities && !isVolumePrimary) {
					setValue("nominatedVolume", undefined);
					updateActionQuantities(undefined, undefined);
				} else {
					updateActionQuantities(undefined, nominatedVolume);
				}
			}
		} else {
			// If unchecking volume primary while weight is primary and convert is enabled, convert from weight
			if (convertCargoQuantities && isWeightPrimary && nominatedWeight != null) {
				const { volume } = convertWeightAndVolume("weight", densityKgM3, nominatedWeight);
				setValue("nominatedVolume", volume);
				updateActionQuantities(nominatedWeight, volume);
			} else {
				setValue("nominatedVolume", undefined);
				// If volume was being used to convert weight, clear weight too
				if (convertCargoQuantities && !isWeightPrimary) {
					setValue("nominatedWeight", undefined);
					updateActionQuantities(undefined, undefined);
				} else {
					updateActionQuantities(nominatedWeight, undefined);
				}
			}

		}
	};

	const handleDeclaredFinalWeightChange = (value?: number) => {
		if (!fillCargoActionQuantities) {
			return;
		}

		const currentActions = getValues("actions");

		const effectiveWeight = value ?? nominatedWeight;
		const effectiveVolume = convertCargoQuantities && effectiveWeight != null
			? Number(Number(effectiveWeight / densityKgM3).toFixed(3))
			: nominatedVolume;

		const updatedActions = distributeWeightAndVolumeOverActions(
			currentActions,
			(isWeightPrimary || convertCargoQuantities) ? effectiveWeight : undefined,
			(isVolumePrimary || convertCargoQuantities) ? effectiveVolume : undefined,
		);

		setValue("actions", updatedActions);
	};

	const onChangeOption = (type: CallType, appendAction: boolean) => {
		if (appendAction) {
			const firstAction = actions.at(0);
			const lastAction = actions.at(-1);

			const newAction = { ...defaultCargoAction(type) };

			const canAddAction: Partial<Record<CallType, boolean>> = {
				[CallType.NitrogenPurging]: firstAction == null || firstAction.type === CallType.Loading,
				[CallType.Cleaning]: lastAction?.type === CallType.Discharging,
			};

			const addAction: Partial<Record<CallType, () => CargoActionForm[]>> = {
				[CallType.NitrogenPurging]: () => [ {
					...newAction,
					limitType: undefined,
					limitValue: undefined,
				}, ...actions ],
				[CallType.Cleaning]: () => [ ...actions, newAction ],
			};

			if (canAddAction[type] && addAction[type]) {
				setValue("actions", addAction[type]());
			}

		} else {
			const newActions = actions.filter(a => a.type !== type);
			setValue("actions", newActions);
		}
	};

	const isDesiredTemperatureEnabled = hasOptionalFieldPermission(session, Fields.ORDER, "desiredTemperature");
	const isSupplierEnabled = hasOptionalFieldPermission(session, Fields.ORDER, "supplier");

	return <Grid container spacing={2} >
		<Grid size={12}>
			<Card title={Language("DETAILS")} >
				<Grid container spacing={2} >
					<Grid size={{ lg: 3, md: 3, xs: 12 }}>
						{isSupplierEnabled && (
							<FormGroup label={Language("SUPPLIER")} overflow="hidden">
								<Controller
									name="supplier"
									control={control}
									render={({ field }) => (
										<RelationSelect
											{...field}
											types={[ RelationType.Supplier ]}
											onChange={option => {
												field.onChange(option ? { id: option.id, name: option.name } : undefined);

												if (option?.id !== supplier?.id) {
													setValue("product", undefined);
												}
											}} />
									)} />
							</FormGroup>
						)}

						<FormGroup label={Language("PRODUCT")} overflow="hidden" required>
							<FormField name="product" errors={formState.errors}>
								<Controller
									name='product'
									control={control}
									rules={{ required: true }}
									render={({ field }) => <ProductSelect {...field} supplierId={supplier?.id} filterLinkedSupplier={isSupplierEnabled} />} />
							</FormField>
						</FormGroup>

						{hasOptionalFieldPermission(session, Fields.ORDER, "deliveryCondition") && (
							<FormGroup label={Language("DELIVERY_CONDITION")} overflow="hidden">
								<Controller
									name="deliveryCondition"
									control={control}
									render={({ field }) => <DeliveryConditionSelect {...field} />} />
							</FormGroup>
						)}

						{hasOptionalFieldPermission(session, Fields.ORDER, "consignor") && (
							<FormGroup label={Language("CARGO_CONSIGNOR")} overflow="hidden">
								<Controller
									name="consignor"
									control={control}
									render={({ field }) => (
										<RelationSelect
											{...field}
											onChange={option => field.onChange(option ? { id: option.id, name: option.name } : null)} />
									)} />
							</FormGroup>
						)}
						{hasOptionalFieldPermission(session, Fields.ORDER, "consignee") && (
							<FormGroup label={Language("ORDER_CONSIGNEE")} overflow="hidden">
								<Controller
									name="consignee"
									control={control}
									render={({ field }) => (
										<RelationSelect
											{...field}
											onChange={option => field.onChange(option ? { id: option.id, name: option.name } : null)} />
									)} />
							</FormGroup>
						)}
					</Grid>
					<Grid size={{ md: hasOptionalFieldPermission(session, Fields.ORDER, "services") ? 4 : 6, xs: 12 }}>
						<Grid container size={12} spacing={2}>
							<Grid size={6}>
								<FormGroup label={Language("CARGO_NOMINATED_WEIGHT")} overflow="hidden">
									<Grid container sx={{ display: "flex", flexWrap: "nowrap", justifyContent: "space-between", alignItems: "center" }}>
										<div style={{ width: 32 }}>
											<Controller
												control={control}
												name="isWeightPrimary"
												render={({ field }) => (
													<Checkbox
														value={undefined}
														onChange={(event) => {
															field.onChange(event.target.checked);
															handlePrimaryCheckboxChange("weight");
														}}
														checked={field.value ?? undefined} />
												)} />
										</div>

										<div style={{ flexGrow: 1, minWidth: 0 }}>
											<Controller
												name="nominatedWeight"
												control={control}
												render={({ field }) =>
													<NumberInput
														value={nominatedWeight || null}
														disabled={!isWeightPrimary}
														onChange={(value) => {
															field.onChange(value);
															handleNominatedQuantityChange("weight", value ?? undefined);
														}}
														decimalScale={3}
														addonAfter={Language("UNIT_TON")} />
												} />
										</div>
									</Grid>
								</FormGroup>
							</Grid>
							<Grid size={6}>
								<FormGroup label={Language("CARGO_NOMINATED_VOLUME")} overflow="hidden">
									<Grid container sx={{ display: "flex", flexWrap: "nowrap", justifyContent: "space-between", alignItems: "center" }}>
										<div style={{ width: 32 }}>
											<Controller control={control}
												name="isVolumePrimary"
												render={({ field }) => (
													<Checkbox
														value={undefined}
														onChange={(event) => {
															field.onChange(event.target.checked);
															handlePrimaryCheckboxChange("volume");
														}}
														checked={field.value ?? undefined} />
												)} />
										</div>
										<div style={{ flexGrow: 1, minWidth: 0 }}>
											<Controller
												name="nominatedVolume"
												control={control}
												render={({ field }) =>
													<NumberInput
														value={nominatedVolume || null}
														disabled={!isVolumePrimary}
														onChange={(value) => {
															field.onChange(value);
															handleNominatedQuantityChange("volume", value ?? undefined);
														}}
														decimalScale={3}
														addonAfter={Language("UNIT_M3")} />
												} />
										</div>
									</Grid>
								</FormGroup>
							</Grid>
							<Grid size={12} sx={{ marginTop: 0, height: 49 }}>
								<Controller
									name="waterLevelOptimised"
									control={control}
									render={({ field }) => (
										<Checkbox
											{...field}
											checked={field.value ?? undefined}
											value={undefined}
											label={Language("WATER_LEVEL_OPTIMISED")} />
									)} />
							</Grid>

							{isDesiredTemperatureEnabled && (
								<Grid size={12} style={{ marginTop: -8 }}>
									{isTemperatureControlRequired && (
										<FormGroup label={Language("NOMINATION_DISCHARGING_TEMPERATURE")} overflow="hidden">
											<Controller
												name="desiredTemperature"
												control={control}
												render={({ field }) =>
													<Input {...field} type="number" addonAfter="°C" />} />
										</FormGroup>
									)}
								</Grid>
							)}
						</Grid>

						{hasOptionalFieldPermission(session, Fields.ORDER, "nominationNumber") && (
							<Grid size={12} style={{ marginTop: isDesiredTemperatureEnabled && isTemperatureControlRequired ? 8 : -8 }}>
								<FormGroup label={Language("NOMINATION_NUMBER")}>
									<Controller
										name="nominationNumber"
										control={control}
										render={({ field }) =>
											<Input {...field} />} />
								</FormGroup>
							</Grid>
						)}

						{hasOptionalFieldPermission(session, Fields.ORDER, "reference") && (
							<Grid size={12} sx={{ mt: 1 }}>
								<FormGroup label={Language("CARGO_REFERENCE")}>
									<Controller
										name="reference"
										control={control}
										render={({ field }) => (
											<Input {...field} />
										)}
									/>
								</FormGroup>
							</Grid>
						)}
					</Grid>

					{hasOptionalFieldPermission(session, Fields.ORDER, "weightTolerance") && (
						<Grid size={{ lg: 3, md: 3, xs: 12 }}>
							<FormGroup label={Language("WEIGHT_TOLERANCE_TYPE")}>
								<Controller
									name="weightToleranceType"
									control={control}
									render={({ field }) =>
										<WeightToleranceTypeSelect
											{...field}
											onChange={(type) => {
												if (!type || ![ WeightToleranceType.CharterersOption, WeightToleranceType.OwnersOption ].includes(type)) {
													setValue("weightOptionType", undefined);
													setValue("weightOptionPercentage", undefined);
													setValue("declaredFinalWeight", undefined);
												}
												field.onChange(type);
											}}
										/>} />
							</FormGroup>

							{(weightToleranceType === WeightToleranceType.OwnersOption || weightToleranceType === WeightToleranceType.CharterersOption) && (
								<div>
									<FormGroup label={Language("WEIGHT_OPTION_TYPE")} overflow="hidden">
										<Grid container spacing={2}>
											<Grid size={{ lg: 8, md: 8, xs: 12 }}>
												<Controller
													name="weightOptionType"
													control={control}
													render={({ field }) => <WeightOptionTypeSelect {...field} />} />
											</Grid>

											<Grid size={{ lg: 4, md: 4, xs: 12 }}>
												<Controller
													name="weightOptionPercentage"
													control={control}
													render={({ field }) => <Input {...field} type="number" addonAfter="%" />} />
											</Grid>
										</Grid>
									</FormGroup>

									<FormGroup label={Language("DECLARED_FINAL_WEIGHT")} overflow="hidden">
										<Controller
											name="declaredFinalWeight"
											control={control}
											render={({ field: { value, onChange } }) => (
												<Input
													value={value || ""}
													onChange={(event) => {
														const value = event.target.value ? Number(event.target.value) : undefined;
														onChange(value);
														handleDeclaredFinalWeightChange(value);
													}}
													type="number"
													addonAfter={Language("UNIT_TON")} />
											)} />
									</FormGroup>
								</div>
							)}
						</Grid>
					)}

					{hasOptionalFieldPermission(session, Fields.ORDER, "services") && (
						<Grid size={{ lg: 2, md: 2, xs: 12 }}>
							<FormGroup label={Language("CARGO_SERVICES")} overflow="hidden">
								<div style={{ marginTop: 8 }}>
									<Controller
										name="isHoseRentalRequired"
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												checked={field.value}
												value={undefined}
												label={Language("HOSE_RENTAL")} />
										)} />
								</div>

								<Controller
									name="isNitrogenPurgingRequired"
									control={control}
									render={({ field: { value, onChange } }) => (
										<Checkbox
											onChange={(ev) => {
												const nitrogenPurgingRequired = ev.target.checked;
												onChangeOption(CallType.NitrogenPurging, nitrogenPurgingRequired);
												onChange(nitrogenPurgingRequired);
											}}
											checked={value}
											value={undefined}
											label={Language("NITROGEN_PURGING")} />
									)} />

								<Controller
									name="isNitrogenBlanketingRequired"
									control={control}
									render={({ field }) => (
										<Checkbox
											{...field}
											checked={field.value}
											value={undefined}
											label={Language("NITROGEN_BLANKETING")} />
									)} />

								<Controller
									name="isInhibitedProduct"
									control={control}
									render={({ field }) => (
										<Checkbox
											{...field}
											checked={field.value}
											value={undefined}
											label={Language("INHIBITED_PRODUCT")} />
									)} />

								<Controller
									name="isTemperatureControlRequired"
									control={control}
									render={({ field }) => (
										<Checkbox
											{...field}
											checked={field.value}
											value={undefined}
											label={Language("TEMPERATURE_CONTROL")} />
									)} />

								<Controller
									name="isCleaningRequired"
									control={control}
									render={({ field: { value, onChange } }) => (
										<Checkbox
											onChange={(ev) => {
												const cleaningRequired = ev.target.checked;
												onChangeOption(CallType.Cleaning, cleaningRequired);
												onChange(cleaningRequired);
											}}
											checked={value}
											value={undefined}
											label={Language("TANK_CLEANING")} />
									)} />
							</FormGroup>
						</Grid>
					)}

					<Grid size={12} sx={{ display: "flex", gap: 1, marginLeft: 1 }}>
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										handleConvertQuantitiesToggle(event.target.checked);
									}}
									checked={convertCargoQuantities && !(isWeightPrimary && isVolumePrimary) && !(!isWeightPrimary && !isVolumePrimary)}
									disabled={(isWeightPrimary && isVolumePrimary) || (!isWeightPrimary && !isVolumePrimary)}
									size="small"
								/>}
							label={(
								<span style={{
									fontSize: 14,
									userSelect: "none",
								}}>
									{intl.formatMessage({ id: "CONVERT_QUANTITIES" })}
								</span>
							)}
						/>
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										handleFillQuantitiesToggle(event.target.checked);
									}}
									checked={fillCargoActionQuantities}
									size="small"
								/>}
							label={(
								<span style={{
									fontSize: 14,
									userSelect: "none",
								}}>
									{intl.formatMessage({ id: "FILL_QUANTITIES_ON_CARGO_ACTIONS" })}
								</span>
							)}
						/>
					</Grid>
				</Grid>
			</Card>
		</Grid>

		<Grid size={12}>
			<Actions
				defaultIncludedActionTypes={defaultIncludedActionTypes}
				calculateWeightAndVolume={convertCargoQuantities}
				control={control}
				formState={formState}
				setValue={setValue}
				validFrom={validFrom}
				validUntil={validUntil}
				getValues={getValues}
				trigger={trigger}
				validateCargoActions={validateCargoActions}
			/>
		</Grid>
	</Grid>;
};

export default CargoCreateEditCustomContent;
